import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from "react-router-dom"
import useTitle from "@/hooks/useTitle"
import {
    PlusIcon,
    MinusIcon,
    PencilSquareIcon,
  } from "@heroicons/react/24/outline"
import PulseLoader from "react-spinners/PulseLoader"
import EtContentEditorLight2 from "@/utils/EtContentEditorLight2"
import EtBtnFormBasic from "@/components/common/buttons/EtBtnFormBasic"
import { useCreateNewUserImprintEtMutation, useGetUserImprintEtsQuery

 } from './userImprintEtsApiSlice'
import { useGetInspirationBeaconsQuery } from '@/features/inspirationBeacons/inspirationBeaconsApiSlice'
import { useGetInspirationBeaconTypesQuery } from '@/features/inspirationBeaconTypes/inspirationBeaconTypesApiSlice'

const EtUpdateUserImprintEt = ({id, userId, userOrgId, setActiveAdminView}) => {

  // First Created - 05 Nov 2024
  // Author - Atul Pandey
  // Updated - 06 Nov 2024 - added checks for useEffect - single load of backend data and availableBeacons
  // Updated - 07 Nov 2024 - added useParams to extract the user and guest ids
  // Updated - 11 May 2025 - refactored for Admin Panel

  // 0 - Set Page title & variables
  useTitle('User Imprint Et - Update')

  // const { userId, userOrgId, guestUserId, guestUserOrgId } = useParams()

  // 1a - set page navigations
  const navigate = useNavigate()

  // 1b - Initialize state variables for the editor key and value
  const [editorKey, setEditorKey] = useState("etDesc-" + Date.now())
  const [editorValue, setEditorValue] = useState("");
  const [selectedBeaconType, setSelectedBeaconType] = useState("");
  const [availableBeacons, setAvailableBeacons] = useState([]);
  const [tempAllNote, setTempAllNote] = useState("");
  const [tempGratitudeNote, setTempGratitudeNote] = useState("");
  const [showAllNotesEditor, setShowAllNotesEditor] = useState(false); // 05 Nov 2024
  const [editAllNoteIndex, setEditAllNoteIndex] = useState(null); // 05 Nov 2024
  const [showGratitudeNotesEditor, setShowGratitudeNotesEditor] = useState(false); // 05 Nov 2024
  const [editGratitudeNoteIndex, setEditGratitudeNoteIndex] = useState(null); // 05 Nov 2024
  const [initialDataLoaded, setInitialDataLoaded] = useState(false); // 06 Nov 2024
  const [selectedBeacons, setSelectedBeacons] = useState([]); // 06 Nov 2024


  // 2a - set the mutation to create the new user imprint et data
  const [createNewUserImprintEt, {
    isLoading,
    isSuccess,
    isError,
    error,
  }] = useCreateNewUserImprintEtMutation()

  // 2b - Fetch the existing record from the backend
  const { data: userImprintEtData} = useGetUserImprintEtsQuery()
  const userImprintEt = userImprintEtData?.ids
   ?.map(id => userImprintEtData?.entities[id])
  //  ?.filter(item => item.userId._id === userId && item.userOrgId._id === userOrgId && item.guestUserId._id === guestUserId && item.guestUserOrgId._id === guestUserOrgId)
   ?.filter(item => item._id === id)

  // console.log('userImprintEt', userImprintEt)

  // 2c - Get Inspiration Beacon Types Data
  const {data: inspirationBeaconTypesData} = useGetInspirationBeaconTypesQuery()
  const inspirationBeaconTypes = inspirationBeaconTypesData?.ids?.map(id => inspirationBeaconTypesData?.entities[id])
  
  // 2c - Get Inspiration Beacons Data
  const {data: inspirationBeaconsData} = useGetInspirationBeaconsQuery()
  const inspirationBeacons = inspirationBeaconsData?.ids?.map(id => inspirationBeaconsData?.entities[id])

  // console.log('Inspiration Beacon Types', inspirationBeaconTypes)
  // console.log('Inspiration Beacons', inspirationBeacons)

  // 3a - Initialize state variables for the form data
  const [formData, setFormData] = useState({
    userImprintEtName: "",
    userImprintEtDesc: "",
    userInspirationBeacons: [], 
    userImprintEtAllNotes: [],
    userImprintEtGratitudeNotes: [],
    aboutMe: "",
    pic: "",
  });

  // 3b - Populate form data when the user imprint is fetched
  useEffect(() => {
    if (userImprintEt && userImprintEt.length > 0 && !initialDataLoaded) {
      const imprint = userImprintEt[0];
      setFormData({
        userImprintEtName: imprint.userImprintEtName || "",
        userImprintEtDesc: imprint.userImprintEtDesc || "",
        userInspirationBeacons: imprint.userInspirationBeacons || [],
        userImprintEtAllNotes: imprint.userImprintEtAllNotes || [],
        userImprintEtGratitudeNotes: imprint.userImprintEtGratitudeNotes || [],
        aboutMe: imprint.aboutMe || "",
        pic: "", // Assume image will be uploaded again if needed
      });
      setEditorValue(imprint.userImprintEtDesc || ""); // Set initial editor content
      setInitialDataLoaded(true); // Prevent further updates
    }
  }, [userImprintEt, initialDataLoaded]);
  // console.log('formData', formData.userInspirationBeacons)
  // console.log('formData gratitude notes', formData.userImprintEtGratitudeNotes)

  // 3c - Initial setup of availableBeacons based on backend data
  useEffect(() => {
    if (inspirationBeaconsData && formData.userInspirationBeacons?.length > 0 && !initialDataLoaded) {
      const initialAvailableBeacons = inspirationBeaconsData?.ids
        ?.map(id => inspirationBeaconsData.entities[id])
        ?.filter(beacon => 
          !formData.userInspirationBeacons.some(
            selectedBeacon => selectedBeacon?.userInspirationBeaconId?._id === beacon._id
          )
        );
      setAvailableBeacons(initialAvailableBeacons || []);
    }
  }, [inspirationBeaconsData, formData.userInspirationBeacons, initialDataLoaded]);

  // 3d - Populate availableBeacons whenever selectedBeaconType changes
  useEffect(() => {
    if (inspirationBeaconsData) {
      const filteredBeacons = inspirationBeaconsData?.ids
        ?.map(id => inspirationBeaconsData?.entities[id])
        ?.filter(beacon => 
          beacon.inspirationBeaconTypeId?._id === selectedBeaconType &&
          !formData.userInspirationBeacons.some(
            selectedBeacon => selectedBeacon?.userInspirationBeaconId?._id === beacon._id
          )
        );

      setAvailableBeacons(filteredBeacons || []);
    }
  }, [selectedBeaconType, formData.userInspirationBeacons, inspirationBeaconsData]);

  // 3e - Populate selectedBeacons
  useEffect(() => {
    if (formData.userInspirationBeacons?.length > 0 && initialDataLoaded) {
      setSelectedBeacons(formData.userInspirationBeacons);
      // console.log("Selected Beacons initially set:", formData.userInspirationBeacons);
    }
  }, [formData.userInspirationBeacons, initialDataLoaded]);

  // 4a - Toggle editor for creating or editing all notes
  const handleAddNewAllNote = () => {
    setTempAllNote("");
    setShowAllNotesEditor(true);
    setEditAllNoteIndex(null); // Not editing an existing note
  };

  const handleEditAllNote = (index) => {
    setTempAllNote(formData.userImprintEtAllNotes[index]?.userImprintEtAllNoteText || "");
    if (!showAllNotesEditor) setShowAllNotesEditor(true);
    setEditAllNoteIndex(index); // Set index to identify which note is being edited
    // setEditorKey("allNotes-" + Date.now())
  };

  // 4b - Similar functions for Gratitude Notes
  const handleAddNewGratitudeNote = () => {
    setTempGratitudeNote("");
    setShowGratitudeNotesEditor(true);
    setEditGratitudeNoteIndex(null);
  };

  const handleEditGratitudeNote = (index) => {
    setTempGratitudeNote(formData.userImprintEtGratitudeNotes[index]?.userImprintEtGratitudeNoteText || "");
    if (!showGratitudeNotesEditor) setShowGratitudeNotesEditor(true);
    setEditGratitudeNoteIndex(index);
    // setEditorKey("gratitudeNotes-" + Date.now())
  };

  // 5a - Function to handle adding a beacon
  const handleAddBeacon = (beaconId) => {
    const newBeacon = inspirationBeacons.find(beacon => beacon._id === beaconId);
  
    // Only add if the beacon exists and is not already in selectedBeacons
    if (newBeacon && !selectedBeacons.some(beacon => beacon._id === beaconId)) {
      setSelectedBeacons(prevBeacons => [
        ...prevBeacons,
        { _id: newBeacon._id, inspirationBeaconName: newBeacon.inspirationBeaconName } // Adjust structure for consistency
      ]);
  
      // Update availableBeacons to remove the added beacon
      setAvailableBeacons(prevBeacons => prevBeacons.filter(b => b._id !== beaconId));
    }
  };
  // console.log('selected Beacons', selectedBeacons) 

  // 5b - Function to handle removing a beacon
  const handleRemoveBeacon = (index) => {
    setSelectedBeacons(prevBeacons => {
      const beaconToRemove = prevBeacons[index];
      
      // Remove the beacon from selectedBeacons
      const updatedBeacons = prevBeacons.filter((_, i) => i !== index);
      
      // Add the removed beacon back to availableBeacons if it exists in inspirationBeacons
      const availableBeacon = inspirationBeacons.find(b => b._id === beaconToRemove.userInspirationBeaconId?._id || b._id === beaconToRemove._id);
      if (availableBeacon) {
        setAvailableBeacons(prevAvailable => [...prevAvailable, availableBeacon]);
      }
      
      return updatedBeacons;
    });
  };

  // 5c - Function to handle selecting all beacons of a type
  // const handleSelectAllBeacons = () => {
  //   setFormData(prevState => {
  //     // Merge current beacons with available ones, removing duplicates
  //     const newBeacons = availableBeacons
  //       ?.filter(beacon => !prevState.userInspirationBeacons.some(selected => selected.userInspirationBeaconId === beacon._id))
  //       ?.map(beacon => ({ userInspirationBeaconId: beacon._id }));
  
  //     return {
  //       ...prevState,
  //       userInspirationBeacons: [...prevState.userInspirationBeacons, ...newBeacons]
  //     };
  //   });
    
  //   // Clear the available beacons as they are now added
  //   setAvailableBeacons([]);
  // };
  const handleSelectAllBeacons = () => {
    const newBeacons = availableBeacons
      ?.filter(beacon => 
        !selectedBeacons.some(selected => selected._id === beacon._id)
      )
      ?.map(beacon => ({ _id: beacon._id, inspirationBeaconName: beacon.inspirationBeaconName }));
  
    // Update selectedBeacons with all available beacons
    setSelectedBeacons(prevBeacons => [...prevBeacons, ...newBeacons]);
  
    // Clear availableBeacons as they are now all added
    setAvailableBeacons([]);
  };
  
  
  // 5d - Save All Note
  const handleSaveAllNote = () => {
    setFormData(prevState => {
        const updatedNotes = [...prevState.userImprintEtAllNotes];
        
        if (editAllNoteIndex !== null) {
            // Editing an existing note
            updatedNotes[editAllNoteIndex] = {
                ...formData.userImprintEtAllNotes[editAllNoteIndex], // Retain existing _id
                userImprintEtAllNoteText: tempAllNote,
                userImprintEtAllNoteUpdateDate: new Date().toISOString() // Update timestamp
            };
        } else {
            // Adding a new note
            updatedNotes.push({
                userImprintEtAllNoteText: tempAllNote,
                userImprintEtAllNoteDate: new Date().toISOString(),
                userImprintEtAllNoteUpdateDate: new Date().toISOString()
            });
        }

        // Remove any empty notes to keep indices consistent with display
        const nonEmptyNotes = updatedNotes.filter(note => note.userImprintEtAllNoteText.trim() !== "");
        return { ...prevState, userImprintEtAllNotes: nonEmptyNotes };
    });

    setTempAllNote("");
    setShowAllNotesEditor(false);
};

  // 5e - Cancel All Note
  const handleCancelAllNote = () => {
    setTempAllNote(""); // Clear temp input
    setShowAllNotesEditor(false);
  };

  // 5f - Save Gratitude Note
    const handleSaveGratitudeNote = () => {
      setFormData(prevState => {
          const updatedNotes = [...prevState.userImprintEtGratitudeNotes];
          
          if (editGratitudeNoteIndex !== null) {
              // Editing an existing note
              updatedNotes[editGratitudeNoteIndex] = {
                  ...formData.userImprintEtGratitudeNotes[editGratitudeNoteIndex], // Retain existing _id
                  userImprintEtGratitudeNoteText: tempGratitudeNote,
                  userImprintEtGratitudeNoteUpdateDate: new Date().toISOString() // Update timestamp
              };
          } else {
              // Adding a new note
              updatedNotes.push({
                  userImprintEtGratitudeNoteText: tempGratitudeNote,
                  userImprintEtGratitudeNoteDate: new Date().toISOString(),
                  userImprintEtGratitudeNoteUpdateDate: new Date().toISOString()
              });
          }

          // Remove any empty notes to keep indices consistent with display
          const nonEmptyNotes = updatedNotes.filter(note => note.userImprintEtGratitudeNoteText.trim() !== "");
          return { ...prevState, userImprintEtGratitudeNotes: nonEmptyNotes };
      });

      setTempGratitudeNote("");
      setShowGratitudeNotesEditor(false);
    };

  // 5g - Cancel Gratitude Note
  const handleCancelGratitudeNote = () => {
    setTempGratitudeNote(""); // Clear temp input
    setShowGratitudeNotesEditor(false);
  };

  // 5h - Handle form field changes
  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "pic") {
      setFormData(prevState => ({ ...prevState, pic: files[0] }));
    } else if (name === "userImprintEtName") {
      setFormData(prevState => ({ ...prevState, userImprintEtName: value }));
    } else {
      setFormData(prevState => ({ ...prevState, [name]: value }));
    }
  };

  // 5i - Handle editor field changes
  const handleEditorChange = (content) => {
    setEditorValue(content);
    setFormData(prevState => ({ ...prevState, userImprintEtDesc: content }));
  };

  // 6 - Form submit logic for updating
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Prepare filtered data for submission
    const imprintData = {
      // _id: userImprintEt[0]?._id, // Include the existing record ID for updating
      imprintId: userImprintEt[0]?._id, // Include the existing record ID for updating
      userId,
      userOrgId,
      // guestUserId, // 11 May 2025 - commented as we are sending imprintId to locate the guest
      // guestUserOrgId, // 11 May 2025 - commented as we are sending imprintId to locate the guest
      userImprintEtName: formData.userImprintEtName,
      userImprintEtDesc: formData.userImprintEtDesc,
      userInspirationBeacons: selectedBeacons.map(beacon => 
        beacon.userInspirationBeaconId ? beacon : { userInspirationBeaconId: beacon._id }
      ),
      userImprintEtAllNotes: formData.userImprintEtAllNotes.filter(
        (note) => note.userImprintEtAllNoteText.trim() !== ""
      ),
      userImprintEtGratitudeNotes: formData.userImprintEtGratitudeNotes.filter(
        (note) => note.userImprintEtGratitudeNoteText.trim() !== ""
      ),
      aboutMe: formData.aboutMe,
    };

    // Handle the optional image upload
    if (formData.pic) {
      const reader = new FileReader();
      reader.onloadend = async () => {
        imprintData.pic = reader.result; // Set base64 image data

        try {
          await createNewUserImprintEt(imprintData).unwrap(); // Create / update mutation
        } catch (error) {
          console.error(error);
        }
      };
      reader.readAsDataURL(formData.pic); // Convert file to base64
    } else {
      try {
        await createNewUserImprintEt(imprintData).unwrap(); // Create / update mutation
      } catch (error) {
        console.error(error);
      }
    }
  };

  // 7 - Reset form upon successful submission
  useEffect(() => {
    if(isSuccess) {
      setFormData({
        userImprintEtName: "",
        userImprintEtDesc: "",
        userInspirationBeacons: [], 
        userImprintEtAllNotes: [],
        userImprintEtGratitudeNotes: [],
        aboutMe: "",
        pic: "",
      })
      setEditorKey("")
      // navigate('/userImprintEts')
      setActiveAdminView('userImprintEts'); 
    }
  
  },[isSuccess]) // ✅ 11 May 2025 - Removed `navigate` since it's no longer used.

  // 8 - Handle Cancel
  const handleCancel = () => {
    setEditorKey("")
    // navigate('/userImprintEts')
    setActiveAdminView('userImprintEts'); 
  }

  // 9 - Handle Clear
  const handleClear = () => {
    setFormData({
        userImprintEtName: "",
        userImprintEtDesc: "",
        userInspirationBeacons: [], 
        userImprintEtAllNotes: [],
        userImprintEtGratitudeNotes: [],
        aboutMe: "",
        pic: "",
    })
  }

  // 10 - create Markup
  const createMarkup = (htmlContent) => {
    return { __html: htmlContent }
  }

  return (
    <div className=" m-1 p-1 mx-auto">
      {isLoading && <PulseLoader color={"#ea580c"} />}
      {isError && (
        <p className="text-orange-700 font-semi-bold py-2">
          {error?.data?.message}
        </p>
      )}

      <div className="bg-white p-1 m-1 rounded shadow-md shadow-black">
        <p className="p-1 m-1 font-bold font-poppins text-xl">Update Et User Imprint</p>

        <form onSubmit={handleSubmit} className="m-1 p-1 text-xs font-poppins">

        {/* Input for Imprint Et Name */}
        <div className="bg-gray-100 rounded p-1 m-1">
          <input
            type="text"
            name="userImprintEtName"
            placeholder="Enter Imprint Et Name"
            value={formData.userImprintEtName}
            onChange={handleChange}
            className="w-full rounded p-2"
          />
        </div>

        {/* Editor for Imprint Et Description */}
        <div className="bg-white rounded p-1 m-1">
          <p className="font-semibold mb-1">Imprint Et Description:</p>
          <EtContentEditorLight2
            value={editorValue}
            handleChange={handleEditorChange}
            placeholderText="Enter description here..."
            editorKey={"desc-"}
          />
        </div>

        {/* Editor for All Notes */}
        <div id="allNotesEditor" className="bg-white rounded py-1 my-1">
          <div className="flex justify-start items-center m-1 p-1">
            <p className="font-semibold mx-1 px-1">All Notes:</p>
            <button
              type="button"
              onClick={handleAddNewAllNote}
              className="bg-lime-500 text-white rounded text-[10px]"
            >
              <PlusIcon className='h-5 w-5 p-1'/> 
            </button>
          </div>

          {showAllNotesEditor && (
          <div className='mt-2'>
            <EtContentEditorLight2
              value={tempAllNote}
              handleChange={(content) => setTempAllNote(content)}
              placeholderText="Enter all notes here..."
              editorKey={"allNotes-" + editorKey} // Unique key
            />
            <div className="flex space-x-2 mt-1">
              <button
                type="button"
                onClick={handleSaveAllNote}
                className="bg-lime-500 text-white rounded px-2 py-1 text-xs"
              >
                Save
              </button>
              <button
                type="button"
                onClick={handleCancelAllNote}
                className="bg-orange-600 text-gray-200 rounded px-2 py-1 text-xs"
              >
                Cancel
              </button>
            </div>
          </div>)}
        </div>

        {/* Display All Notes List */}
        <div className="mt-2">
          {formData.userImprintEtAllNotes?.length > 0 && formData.userImprintEtAllNotes
          ?.filter(note => note.userImprintEtAllNoteText?.trim() !== "") 
          ?.map((note, allNoteIndex) => (
            <div key={allNoteIndex} className="border rounded p-1 m-1 flex justify-between items-center">
              <p dangerouslySetInnerHTML={createMarkup(note?.userImprintEtAllNoteText)} className='text-xs'></p>
              <button
                type="button"
                onClick={() => handleEditAllNote(allNoteIndex)}
                className='m-1 p-1'
              >
                <PencilSquareIcon className='h-4 w-4 text-orange-600'/>
              </button>
            </div>
          ))}
        </div>

        {/* Editor for Gratitude Notes */}
        <div id="gratitudeNotesEditor" className="bg-white rounded py-1 my-1">
          <div className="flex justify-start items-center m-1 p-1">
              <p className="font-semibold mx-1 px-1">Gratitude Notes:</p>
              <button
                type="button"
                onClick={handleAddNewGratitudeNote}
                className="bg-lime-500 text-white rounded text-[10px]"
              >
                <PlusIcon className='h-5 w-5 p-1'/>
              </button>
            </div>
          
          {showGratitudeNotesEditor && (
          <div className='mt-2'>
            <EtContentEditorLight2
              value={tempGratitudeNote}
              handleChange={(content) => setTempGratitudeNote(content)}
              placeholderText="Enter gratitude notes here..."
              editorKey={"gratitudeNotes-" + editorKey} // Unique key
            />
            <div className="flex space-x-2 mt-1">
              <button
                type="button"
                onClick={handleSaveGratitudeNote}
                className="bg-lime-500 text-white rounded px-2 py-1 text-xs"
              >
                Save
              </button>
              <button
                type="button"
                onClick={handleCancelGratitudeNote}
                className="bg-orange-600 text-gray-200 rounded px-2 py-1 text-xs"
              >
                Cancel
              </button>
            </div>
          </div>)}

        </div>

        {/* Display Gratitude Notes List */}
        <div className="mt-2">
          {formData.userImprintEtGratitudeNotes
          ?.filter(note => note.userImprintEtGratitudeNoteText?.trim() !== "") 
          ?.map((note, index) => (
            <div key={index} className="border rounded p-1 m-1 flex justify-between items-center">
              <p dangerouslySetInnerHTML={createMarkup(note?.userImprintEtGratitudeNoteText)} className='text-xs'></p>
              <button
                type="button"
                onClick={() => handleEditGratitudeNote(index)}
                className="m-1 p-1"
              >
                <PencilSquareIcon className='h-4 w-4 text-orange-600'/>
              </button>
            </div>
          ))}
        </div>
    
          <div id="beaconsSelect" className="bg-gray-100 rounded p-1 m-1">
            
            <div className='flex justify-start items-center'>
              <p className="inline-block font-semibold mx-1 px-1">Select Beacon Type:</p>
              <select
                  value={selectedBeaconType}
                  onChange={(e) => setSelectedBeaconType(e.target.value)}
                  className="rounded p-2 m-1"
                >
                  <option value="">Choose a Beacon Type</option>
                  {inspirationBeaconTypes?.map((type) => (
                    <option key={type._id} value={type._id}>{type.inspirationBeaconTypeName}</option>
                  ))}
                </select>
            </div>
            
            {/* Available Beacons */}
            <p className="inline-block font-semibold m-1 p-1">Available Beacons:</p>
            {availableBeacons?.length > 0 && (
              <div className="flex justify-start items-center">
                <button
                  type="button"
                  onClick={handleSelectAllBeacons}
                  className="bg-lime-500 hover:bg-lime-700 text-white rounded p-1 m-1"
                >
                  Add All
                </button>
                <select
                  onChange={(e) => handleAddBeacon(e.target.value)}
                  className="rounded p-2 m-1 border"
                  value=""
                >
                  <option value="">Select a beacon to add</option>
                  {availableBeacons?.map(beacon => (
                    <option key={beacon?._id} value={beacon?._id}>{beacon?.inspirationBeaconName}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Selected Beacons */}
            <p className="inline-block font-semibold m-1 p-1">Selected Beacons:</p>
            {selectedBeacons?.map((beacon, index) => (
              <div key={beacon?.userInspirationBeaconId?._id || beacon?._id} className="flex items-center justify-start mx-1 px-1">
                <button
                  type="button"
                  onClick={() => handleRemoveBeacon(index)}
                  className="bg-red-400 hover:bg-red-600 rounded p-1"
                >
                  <MinusIcon className="h-5 w-5 text-white" />
                </button>
                <div className="m-1 p-1 border rounded w-full text-[10px]">
                  <span className="px-1">
                    {beacon.userInspirationBeaconId 
                      ? beacon?.userInspirationBeaconId?.inspirationBeaconName 
                      : beacon?.inspirationBeaconName}
                  </span>
                </div>
              </div>
            ))}
          </div>


          <div id="actions" className="bg-white m-1 px-1 py-3 rounded">
              <EtBtnFormBasic
                name={"Update"}
                type={"create"}
                handleClick={handleSubmit}
              />
              <EtBtnFormBasic
                name={"Clear"}
                type={"clear"}
                handleClick={handleClear}
              />
              <EtBtnFormBasic
                name={"Cancel"}
                type={"cancel"}
                handleClick={handleCancel}
              />
            </div>
        </form>

      </div>

    </div>
  )
}

export default EtUpdateUserImprintEt